import React from 'react';
import { createPortal } from 'react-dom';
import { Sparkles, X, Send, Check, ImageIcon, Maximize2, Search } from 'lucide-react';
import { ClarificationQuestion, NewSearchMode } from './types';
import chatService from '../../../services/ChatService';
import { useActiveSessionIdSafe } from '../../../contexts/SessionContext';

// ── Chat about one clarification question ─────────────────────────────────────
// Two surfaces, one conversation (the thread lives in StructuredQuestionForm):
//   • ClarificationChatCard — one row under the question's options: type or
//     attach a photo.
//   • ClarificationChatPanel — the right-hand sidebar with the conversation,
//     the question being answered, and the assistant's suggestion + Apply.
// The assistant answers from the question's options, the vehicle and what the
// session knows; a suggestion is applied only when the user presses Apply,
// which answers the question like picking that option.

const PRIMARY = '#6866ff';
const PRIMARY_SOFT = '#ededff';
const PRIMARY_DISABLED = '#c5c4ff';
const INK = '#201e1a';
const MUTED = '#6c6978';
const BORDER = '#e7e5f5';

type T = (key: string, opts?: any) => string;

export interface ChatMsg {
  role: 'user' | 'assistant';
  content: string;
  /** data:image/… — a photo the user attached. */
  image?: string | null;
  suggestedOptionId?: string | null;
  /** A different part the assistant offered to search for (one-click CTA). */
  newSearch?: string | null;
  /** "replace" — the customer meant that part INSTEAD of the asked one; "add" — as well. */
  newSearchMode?: NewSearchMode | null;
  suggestedLabel?: string | null;
}

/** Downsize a picked photo to a JPEG data URL (≤ 1280px) before it is sent. */
export const downscaleImage = (file: File, max = 1280): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Not an image'));
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(String(reader.result)); return; }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });

/** The first image in a paste (a screenshot, a copied photo), or null —
 *  plain-text pastes return null and paste normally. */
export const pastedImage = (e: React.ClipboardEvent): File | null => {
  const items = Array.from(e.clipboardData?.items || []);
  for (const it of items) {
    if (it.kind === 'file' && it.type.startsWith('image/')) {
      const f = it.getAsFile();
      if (f) return f;
    }
  }
  const files = Array.from(e.clipboardData?.files || []);
  return files.find(f => f.type.startsWith('image/')) || null;
};

/** onPaste handler: an image is downscaled and handed to `onImage`; text
 *  pastes are left alone. */
const onPasteImage = (onImage: (dataUrl: string) => void) => async (e: React.ClipboardEvent) => {
  const file = pastedImage(e);
  if (!file) return;
  e.preventDefault();
  try { onImage(await downscaleImage(file)); } catch { /* not a readable image — ignore */ }
};

/** Thumbnail of an attached photo with a remove button. */
const AttachedThumb: React.FC<{ src: string; onRemove: () => void; t: T; size?: number }> = ({ src, onRemove, t, size = 44 }) => (
  <div data-testid="attached-photo" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: 3, border: `1px solid ${BORDER}`, borderRadius: 10, flexShrink: 0 }}>
    <img src={src} alt="" style={{ width: size, height: size, objectFit: 'cover', borderRadius: 7 }} />
    <button type="button" onClick={onRemove} aria-label={t('common.remove', { defaultValue: 'Remove' })}
      style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: MUTED, display: 'inline-flex', padding: 2 }}>
      <X size={14} />
    </button>
  </div>
);

/** "**bold**" → <strong>; everything else verbatim. */
const renderRich = (text: string): React.ReactNode =>
  text.split(/(\*\*[^*]+\*\*)/g).map((chunk, i) =>
    chunk.startsWith('**') && chunk.endsWith('**') && chunk.length > 4
      ? <strong key={i} style={{ fontWeight: 700 }}>{chunk.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{chunk}</React.Fragment>);

const iconButton = (active = false): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36,
  border: `1.5px solid ${active ? PRIMARY : 'transparent'}`, borderRadius: 10,
  background: active ? PRIMARY_SOFT : 'transparent', color: active ? PRIMARY : '#3d3b45', cursor: 'pointer', flexShrink: 0,
});
const pillButton: React.CSSProperties = {
  flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 14px',
  border: `1px solid #d9d7e3`, borderRadius: 999, background: '#ffffff', color: INK,
  fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
};
const sendButton = (enabled: boolean): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40,
  border: 'none', borderRadius: 10, background: enabled ? PRIMARY : PRIMARY_DISABLED, color: '#ffffff',
  cursor: enabled ? 'pointer' : 'default', flexShrink: 0,
});

/** Hidden file input + a trigger. */
const usePhotoPicker = (onPicked: (dataUrl: string) => void) => {
  const ref = React.useRef<HTMLInputElement | null>(null);
  const input = (
    <input
      ref={ref}
      type="file"
      accept="image/*"
      style={{ display: 'none' }}
      onChange={async e => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        try { onPicked(await downscaleImage(file)); } catch { /* not an image — ignore */ }
      }}
    />
  );
  return { input, open: () => ref.current?.click() };
};

// ── Row on the question ──────────────────────────────────────────────────────
// One compact line under the options, as in the question card design: sparkle,
// "Something else — describe it in your words or ask…", photo, send. Sending
// opens the sidebar with the conversation.

export interface ClarificationChatCardProps {
  question: ClarificationQuestion;
  messageCount: number;
  onSend: (text: string, image?: string | null) => void;
  t: T;
  /** Reopen the sidebar with the conversation so far. */
  onOpen?: () => void;
}

export const ClarificationChatCard: React.FC<ClarificationChatCardProps> = ({ messageCount, onSend, onOpen, t }) => {
  const [draft, setDraft] = React.useState('');
  // A picked or pasted photo waits here (thumbnail in the row) and goes out
  // with the text on send, so the user can add words to it first.
  const [attached, setAttached] = React.useState<string | null>(null);
  const photo = usePhotoPicker(setAttached);
  const canSend = !!draft.trim() || !!attached;
  const submit = () => {
    if (!canSend) return;
    onSend(draft.trim(), attached);
    setDraft('');
    setAttached(null);
  };
  return (
    <form
      data-testid="clarification-ask"
      onSubmit={e => { e.preventDefault(); submit(); }}
      // Same row geometry as the numbered options above it (OPTION_ROW in
      // StructuredQuestionForm): 14px 8px padding, a 36px tile, 20px gap, 15px
      // text — so the placeholder starts where the option labels start.
      style={{ display: 'flex', alignItems: 'center', gap: 20, padding: '14px 8px' }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 8, background: '#f1f0f6', color: PRIMARY, flexShrink: 0 }}>
        <Sparkles size={16} />
      </span>
      {attached && <AttachedThumb src={attached} onRemove={() => setAttached(null)} t={t} size={30} />}
      <input
        type="text"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onPaste={onPasteImage(setAttached)}
        placeholder={t('chat.clarification.chat.ask', { defaultValue: 'Add additional context about the part to fine-tune the search results…' })}
        style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', fontSize: 15, color: INK, background: 'transparent', fontFamily: 'inherit', padding: '8px 0' }}
      />
      {/* The conversation so far — a click reopens it in the sidebar. */}
      {messageCount > 0 && (
        <button type="button" data-testid="reopen-chat" onClick={onOpen} disabled={!onOpen}
          style={{ border: 'none', background: 'transparent', padding: '4px 2px', fontSize: 12, fontWeight: 600, color: PRIMARY, whiteSpace: 'nowrap', cursor: onOpen ? 'pointer' : 'default', fontFamily: 'inherit', textDecoration: onOpen ? 'underline' : 'none', textUnderlineOffset: 3 }}>
          {t('chat.clarification.chat.open', { defaultValue: 'Chat open · {{count}}', count: messageCount })}
        </button>
      )}
      <button type="button" onClick={photo.open} style={{ ...iconButton(!!attached), width: 32, height: 32, color: attached ? PRIMARY : MUTED }}
        aria-label={t('chat.clarification.chat.photo', { defaultValue: 'Send a photo' })}>
        <ImageIcon size={16} />
      </button>
      <button type="submit" disabled={!canSend} style={{ ...sendButton(canSend), width: 34, height: 34 }}
        aria-label={t('chat.clarification.chat.send', { defaultValue: 'Send' })}>
        <Send size={15} />
      </button>
      {photo.input}
    </form>
  );
};

// ── Sidebar ─────────────────────────────────────────────────────────────────

export interface ClarificationChatPanelProps {
  question: ClarificationQuestion;
  /** Mono line under the question (VIN · order number). */
  context?: string | null;
  messages: ChatMsg[];
  loading: boolean;
  error: boolean;
  onSend: (text: string, image?: string | null) => void;
  /** Answer the question with this option (resolves it and resumes the order). */
  onApply: (optionId: string) => void;
  applyDisabled?: boolean;
  onClose: () => void;
  /** Open the question's EPC diagram (on this option when given). Absent = no
   *  "Diagram" button and no "Show this on the diagram" chip. */
  onOpenDiagram?: (optionId?: string) => void;
  /** Run the search a reply offered (the CTA under a reply that found the
   *  customer needs a different part). "replace" swaps the asked part for it
   *  and resumes the order; "add" searches it in the main chat. */
  onNewSearch?: (query: string, mode: NewSearchMode) => void;
  /** False when an extra ("add") search has nowhere to run — its CTA is hidden;
   *  a "replace" still works (it answers this question). Default true. */
  canAddSearch?: boolean;
  t: T;
}

/** The section drawing behind the question, for the header thumbnail. */
export const useQuestionThumbnail = (question: ClarificationQuestion): string | null => {
  const sessionId = useActiveSessionIdSafe() || chatService.getGuestSessionId();
  const ref = React.useMemo(() => {
    for (const o of question.options || []) {
      const r = (o.load || []).find(x => x.group_id && x.section_id);
      if (r) return r;
    }
    return null;
  }, [question]);
  const [url, setUrl] = React.useState<string | null>(null);
  React.useEffect(() => {
    let cancelled = false;
    if (!sessionId || !ref) { setUrl(null); return; }
    chatService.getSectionDiagram(sessionId, ref.group_id, ref.section_id, ref.partId || undefined)
      .then(d => { if (!cancelled) setUrl(d?.diagram_url || (d as any)?.sheets?.[0]?.diagram_url || null); })
      .catch(() => { if (!cancelled) setUrl(null); });
    return () => { cancelled = true; };
  }, [sessionId, ref]);
  return url;
};

/** Quick replies above the reply box: see the suggestion on the drawing, and
 *  compare the two leading candidates by their diagram positions. */
const quickActions = (question: ClarificationQuestion, messages: ChatMsg[]): { suggestion: string | null; compare: string | null } => {
  let suggestion: string | null = null;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'assistant') { suggestion = messages[i].suggestedOptionId || null; break; }
  }
  const real = (question.options || []).filter(o => !o.extra_details?._expert_action);
  const tag = (o: any): string => String(o?.load?.[0]?.callout || '').trim() || String(o?.label || '').trim();
  const compare = real.length >= 2 && tag(real[0]) && tag(real[1]) ? `${tag(real[0])} vs ${tag(real[1])}` : null;
  return { suggestion, compare };
};

const ClarificationChatPanel: React.FC<ClarificationChatPanelProps> = ({
  question, messages, loading, error, onSend, onApply, applyDisabled, onClose, onOpenDiagram, onNewSearch,
  canAddSearch = true, t,
}) => {
  const actions = quickActions(question, messages);
  const [draft, setDraft] = React.useState('');
  const [attached, setAttached] = React.useState<string | null>(null);
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const photo = usePhotoPicker(setAttached);

  React.useEffect(() => {
    listRef.current?.scrollTo?.({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const canSend = (!!draft.trim() || !!attached) && !loading;
  const submit = () => {
    if (!canSend) return;
    onSend(draft.trim(), attached);
    setDraft('');
    setAttached(null);
  };
  const lastSuggestion = (() => {
    for (let i = messages.length - 1; i >= 0; i--) if (messages[i].suggestedOptionId) return i;
    return -1;
  })();
  // Only the latest offered search is live — an older offer was superseded by
  // what the conversation said after it.
  const lastSearch = (() => {
    for (let i = messages.length - 1; i >= 0; i--) if (messages[i].newSearch) return i;
    return -1;
  })();
  const lastUser = [...messages].reverse().find(m => m.role === 'user');
  const bubble: React.CSSProperties = {
    maxWidth: '86%', padding: '12px 16px', borderRadius: 14, fontSize: 14, lineHeight: 1.5,
    whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
  };

  const panel = (
    <aside
      role="dialog"
      aria-label={t('chat.clarification.chat.title', { defaultValue: 'AI Assistant chat' })}
      data-testid="clarification-chat-panel"
      style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(440px, 100vw)', zIndex: 2147483000,
        display: 'flex', flexDirection: 'column', background: '#ffffff',
        borderLeft: `1px solid ${BORDER}`, boxShadow: '-12px 0 32px rgba(32,30,26,0.08)', fontFamily: 'inherit',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '22px 22px 16px' }}>
        <Sparkles size={18} color={PRIMARY} />
        <span style={{ flex: 1, fontSize: 17, fontWeight: 700, color: INK }}>
          {t('chat.clarification.chat.title', { defaultValue: 'AI Assistant chat' })}
        </span>
        <button type="button" onClick={onClose} aria-label={t('common.close', { defaultValue: 'Close' })}
          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#3d3b45', display: 'inline-flex', padding: 4 }}>
          <X size={20} />
        </button>
      </header>

      <div style={{ padding: '0 22px 16px', borderBottom: `1px solid ${BORDER}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', border: `1px solid ${BORDER}`, borderRadius: 12, background: '#faf9fc' }}>
          {/* Just the question being answered — no drawing tile, no VIN /
              order line, no Diagram button (the card below the question has
              the diagram; the header was too much information). */}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: MUTED }}>
              {t('chat.clarification.chat.answering', { defaultValue: 'Answering' })}
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: INK, marginTop: 2, overflowWrap: 'anywhere' }}>
              {question.text || question.part_label || question.part_name}
            </div>
          </div>
        </div>
      </div>

      <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 14, background: '#fafafd' }}>
        {messages.length === 0 && !loading && (
          <p style={{ fontSize: 13, color: MUTED, margin: 0 }}>
            {t('chat.clarification.chat.empty', { defaultValue: 'Describe the part in your own words, or ask what separates the options.' })}
          </p>
        )}
        {messages.map((m, i) => (
          <React.Fragment key={i}>
            {m.role === 'user' && m.image && (
              <div style={{ alignSelf: 'flex-end', maxWidth: '62%', padding: 8, borderRadius: 14, background: PRIMARY_SOFT, border: `1px solid ${BORDER}` }}>
                <img src={m.image} alt={t('chat.clarification.chat.photoAlt', { defaultValue: 'Photo of the damage' })}
                  style={{ display: 'block', width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 10 }} />
              </div>
            )}
            {m.content && (
              <div style={{
                ...bubble,
                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                ...(m.role === 'user'
                  ? { background: PRIMARY, color: '#ffffff' }
                  : { background: '#ffffff', color: INK, border: `1px solid ${BORDER}` }),
              }}>
                {m.role === 'assistant' ? renderRich(m.content) : m.content}
              </div>
            )}
            {m.role === 'assistant' && m.newSearch && onNewSearch
              && (m.newSearchMode === 'replace' || canAddSearch) && (() => {
              const replace = m.newSearchMode === 'replace';
              const live = i === lastSearch && !loading;
              return (
                <button
                  type="button"
                  data-testid="clarification-chat-new-search"
                  data-mode={replace ? 'replace' : 'add'}
                  disabled={!live}
                  onClick={() => onNewSearch(m.newSearch as string, replace ? 'replace' : 'add')}
                  style={{
                    alignSelf: 'flex-start', maxWidth: '86%', display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '9px 14px', border: 'none', borderRadius: 10,
                    background: live ? PRIMARY : PRIMARY_DISABLED, color: '#ffffff',
                    fontSize: 13.5, fontWeight: 700, cursor: live ? 'pointer' : 'default', fontFamily: 'inherit', textAlign: 'left',
                  }}
                >
                  <Search size={14} aria-hidden="true" />
                  {replace
                    ? t('chat.clarification.chat.searchInstead', { defaultValue: 'Yes, search for “{{query}}” instead', query: m.newSearch })
                    : t('chat.clarification.chat.searchFor', { defaultValue: 'Search for “{{query}}”', query: m.newSearch })}
                </button>
              );
            })()}
            {m.role === 'assistant' && m.suggestedOptionId && (
              <div data-testid="clarification-chat-suggestion" style={{
                alignSelf: 'flex-start', maxWidth: '86%', display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 12px', border: `1.5px solid ${PRIMARY}`, borderRadius: 12, background: '#f7f6ff',
                opacity: i === lastSuggestion ? 1 : 0.55,
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: PRIMARY }}>
                    {t('chat.clarification.chat.suggested', { defaultValue: 'Suggested' })}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: INK, overflowWrap: 'anywhere' }}>
                    {m.suggestedLabel || m.suggestedOptionId}
                  </div>
                </div>
                <button
                  type="button"
                  disabled={applyDisabled || i !== lastSuggestion}
                  onClick={() => onApply(m.suggestedOptionId as string)}
                  style={{
                    flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 14px',
                    border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, color: '#ffffff',
                    background: applyDisabled || i !== lastSuggestion ? PRIMARY_DISABLED : PRIMARY,
                    cursor: applyDisabled || i !== lastSuggestion ? 'default' : 'pointer',
                  }}
                >
                  <Check size={14} />
                  {t('chat.clarification.chat.apply', { defaultValue: 'Apply' })}
                </button>
              </div>
            )}
          </React.Fragment>
        ))}
        {loading && (
          <div style={{ ...bubble, alignSelf: 'flex-start', color: MUTED, background: '#ffffff', border: `1px solid ${BORDER}` }}>
            {lastUser?.image
              ? t('chat.clarification.chat.lookingAtPhoto', { defaultValue: 'Looking at the photo against the drawing…' })
              : t('chat.clarification.chat.thinking', { defaultValue: 'Checking the options…' })}
          </div>
        )}
        {error && (
          <div style={{ alignSelf: 'flex-start', fontSize: 13, color: '#b42318' }}>
            {t('chat.clarification.chat.error', { defaultValue: 'I could not answer right now — try again in a moment.' })}
          </div>
        )}
      </div>

      <footer style={{ padding: '14px 22px 18px', borderTop: `1px solid ${BORDER}` }}>
        {!loading && ((onOpenDiagram && actions.suggestion) || actions.compare) && (
          <div data-testid="chat-quick-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            {onOpenDiagram && actions.suggestion && (
              <button type="button" onClick={() => onOpenDiagram(actions.suggestion || undefined)} style={pillButton}>
                <Maximize2 size={15} />
                {t('chat.clarification.chat.showOnDiagram', { defaultValue: 'Show this on the diagram' })}
              </button>
            )}
            {actions.compare && (
              <button type="button" onClick={() => onSend(t('chat.clarification.chat.compareMessage', { defaultValue: 'Compare {{pair}}', pair: actions.compare }))} style={pillButton}>
                {t('chat.clarification.chat.compare', { defaultValue: 'Compare {{pair}}', pair: actions.compare })}
              </button>
            )}
          </div>
        )}
        {attached && (
          <div style={{ marginBottom: 8 }}>
            <AttachedThumb src={attached} onRemove={() => setAttached(null)} t={t} />
          </div>
        )}
        <form
          onSubmit={e => { e.preventDefault(); submit(); }}
          style={{ display: 'flex', alignItems: 'center', gap: 6, border: `1px solid ${BORDER}`, borderRadius: 14, padding: '5px 5px 5px 16px' }}
        >
          <input
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onPaste={onPasteImage(setAttached)}
            placeholder={t('chat.clarification.chat.reply', { defaultValue: 'Reply…' })}
            autoFocus
            style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', fontSize: 14, color: INK, fontFamily: 'inherit', background: 'transparent', padding: '8px 0' }}
          />
          <button type="button" onClick={photo.open} style={iconButton(!!attached)}
            aria-label={t('chat.clarification.chat.photo', { defaultValue: 'Send a photo' })}>
            <ImageIcon size={18} />
          </button>
          <button type="submit" disabled={!canSend} style={sendButton(canSend)}
            aria-label={t('chat.clarification.chat.send', { defaultValue: 'Send' })}>
            <Send size={16} />
          </button>
          {photo.input}
        </form>
        <p style={{ margin: '10px 0 0', fontSize: 12, color: MUTED }}>
          {t('chat.clarification.chat.applyNote', { defaultValue: 'Applying an answer resolves the question and resumes the order.' })}
        </p>
      </footer>
    </aside>
  );
  return typeof document !== 'undefined' ? createPortal(panel, document.body) : panel;
};

/** A small picture of the question's section drawing (a plain image tile
 *  until it loads, or when there is none). */
export const QuestionDiagramThumb: React.FC<{ question: ClarificationQuestion; width: number; height: number }> = ({ question, width, height }) => {
  const url = useQuestionThumbnail(question);
  return (
    <span data-testid="diagram-thumb" style={{ width, height, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, overflow: 'hidden', background: url ? '#ffffff' : '#e3e2ea', border: '1px solid #e3e2ea', color: '#6c6978' }}>
      {url ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <ImageIcon size={14} />}
    </span>
  );
};

export default ClarificationChatPanel;
